/**
 * GoogleSignInButton — "Continue with Google" for the server-side
 * authorization-code flow.
 *
 *  Behaviour:
 *    1. On mount, fetch `/auth/google/config`. If `enabled === false`
 *       the button doesn't render at all.
 *    2. Clicking it navigates the whole browser to the backend's
 *       `/auth/google/start`, which bounces to Google's consent screen.
 *       Google redirects back to the backend callback, which signs the
 *       user in and redirects into the app. No Google script runs in the
 *       page and the credential never touches JS.
 *    3. Multi-company case: the backend can't pick which company, so it
 *       redirects back here with `?google=choose` and a short-lived
 *       httpOnly cookie holding the verified token. We fetch the account
 *       list from `/auth/google/complete` and show the picker; choosing
 *       one finishes the sign-in.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { api, apiBaseUrl, extractApiError } from '../lib/api'
import { googleSsoPublicConfig } from '../lib/types'
import { AccountAvatar } from './AccountAvatar'

export function GoogleSignInButton({
  mode = 'signin',
  onSuccess,
  onPickerChange,
  // Referral code from `?ref=CODE` — forwarded on a signup so the backend can
  // attribute the referral. Null/undefined on the sign-in page.
  referralCode = null,
}) {
  const [config, setConfig] = useState(null)
  const [busy, setBusy] = useState(false)
  // Account picker — { accounts } when one Google identity matches
  // accounts in several companies and the user must choose which.
  const [picker, setPicker] = useState(null)
  // Guards the one-time account-list fetch on the `?google=choose` return
  // so a re-render (e.g. an unstable onSuccess prop) can't double-post.
  const choosePostedRef = useRef(false)

  // Fetch public config — if SSO is disabled the button stays unmounted.
  useEffect(() => {
    let cancelled = false
    api
      .get('/auth/google/config')
      .then((res) => {
        if (cancelled) return
        const parsed = googleSsoPublicConfig.safeParse(res.data)
        setConfig(parsed.success ? parsed.data : { enabled: false, clientId: '' })
      })
      .catch(() => {
        if (!cancelled) setConfig({ enabled: false, clientId: '' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Choose which company to sign into (multi-company identity). The
  // verified token is read from the httpOnly pending cookie server-side —
  // the credential never reaches the page.
  const chooseGoogleAccount = useCallback(
    async (tenantId) => {
      setBusy(true)
      try {
        const res = await api.post('/auth/google/complete', { tenantId })
        if (res.data?.multipleAccounts) {
          setPicker({
            accounts: res.data.accounts ?? [],
            blockedAccounts: res.data.blockedAccounts ?? [],
          })
          return
        }
        onSuccess?.(res.data)
      } catch (err) {
        const { message } = extractApiError(err)
        toast.error(message || 'Google sign-in failed. Please try again.')
      } finally {
        setBusy(false)
      }
    },
    [onSuccess],
  )

  // Returning from Google with a multi-company identity: the backend set
  // `?google=choose` and stashed the verified token in a cookie. Fetch the
  // account list (no tenantId) to show the picker.
  useEffect(() => {
    if (!config?.enabled) return undefined
    const params = new URLSearchParams(window.location.search)
    if (params.get('google') !== 'choose') return undefined
    if (choosePostedRef.current) return undefined
    choosePostedRef.current = true
    let cancelled = false
    setBusy(true)
    api
      .post('/auth/google/complete', {})
      .then((res) => {
        if (cancelled) return
        if (res.data?.multipleAccounts) {
          setPicker({
            accounts: res.data.accounts ?? [],
            blockedAccounts: res.data.blockedAccounts ?? [],
          })
        } else if (res.data?.user) {
          onSuccess?.(res.data)
        }
      })
      .catch((err) => {
        if (cancelled) return
        const { message } = extractApiError(err)
        toast.error(message || 'Google sign-in failed. Please try again.')
      })
      .finally(() => {
        if (!cancelled) setBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [config, onSuccess])

  // Tell the parent whenever the picker opens/closes so it can hide the
  // email/password form — Google has already authenticated the user, so
  // only the "choose a company" step should remain on screen.
  useEffect(() => {
    onPickerChange?.(Boolean(picker))
  }, [picker, onPickerChange])

  // Kick off the flow — a full-page navigation to the backend start route.
  // `apiBaseUrl` is the same base the app calls the API on, so this works
  // for both same-origin and api-subdomain deploys.
  function startGoogle() {
    const params = new URLSearchParams({ mode })
    if (mode === 'signup' && referralCode) params.set('ref', referralCode)
    window.location.assign(
      `${apiBaseUrl}/auth/google/start?${params.toString()}`,
    )
  }

  if (!config || !config.enabled) return null

  if (picker) {
    return (
      <div className="w-full">
        <GoogleAccountPicker
          accounts={picker.accounts}
          blockedAccounts={picker.blockedAccounts ?? []}
          busy={busy}
          onChoose={chooseGoogleAccount}
          onCancel={() => setPicker(null)}
        />
      </div>
    )
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={startGoogle}
        disabled={busy}
        className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg bg-white border border-[color:var(--color-rule)] text-[color:var(--color-ink)] text-sm font-medium hover:bg-[color:var(--color-card-hover)] disabled:opacity-60 disabled:cursor-wait cursor-pointer transition-colors"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        {mode === 'signup' ? 'Sign up with Google' : 'Continue with Google'}
      </button>
    </div>
  )
}

function pickerRoleText(acc) {
  if (acc.role === 'super_admin') return 'Master Admin'
  if (acc.role === 'company_admin') return 'Company Admin'
  if (acc.role === 'sub_admin') return 'Sub Admin'
  return acc.workRoleName || 'User'
}

/** Picker shown when a Google identity matches accounts in several
 *  companies — choosing one finishes the sign-in scoped to that tenant
 *  (the verified token is read from the httpOnly cookie server-side). */
function GoogleAccountPicker({
  accounts,
  blockedAccounts = [],
  busy,
  onChoose,
  onCancel,
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-[color:var(--color-ink-muted)]">
        This Google account is registered to more than one company. Choose
        which account to continue with.
      </p>
      <div className="space-y-2">
        {accounts.map((acc) => (
          <button
            key={acc.userId}
            type="button"
            disabled={busy}
            onClick={() => onChoose(acc.tenantId)}
            className="w-full flex items-center gap-3 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-4 py-3 text-left transition-colors hover:border-[color:var(--color-primary)] disabled:opacity-60 disabled:cursor-wait cursor-pointer"
          >
            <AccountAvatar name={acc.tenantName} size={40} />
            <span className="min-w-0 flex-1">
              {/* The Google account is the same on every card, so each card
               *  leads with its COMPANY and the person's role there. */}
              <span className="block truncate font-semibold text-[color:var(--color-ink-strong)]">
                {acc.tenantName || acc.fullName || acc.emailId}
              </span>
              <span className="block truncate text-sm text-[color:var(--color-ink-muted)]">
                {pickerRoleText(acc)}
              </span>
            </span>
          </button>
        ))}
      </div>

      {/* Memberships that can't sign in (suspended / company
       *  unavailable) — visible with the reason instead of silently
       *  missing, matching the email/password picker (QA). */}
      {blockedAccounts.length > 0 ? (
        <div className="space-y-2">
          {blockedAccounts.map((acc) => (
            <div
              key={acc.userId}
              aria-disabled="true"
              title={acc.reason ?? ''}
              className="w-full flex items-center gap-3 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-4 py-3 text-left opacity-60 cursor-not-allowed"
            >
              <AccountAvatar name={acc.tenantName} size={40} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-[color:var(--color-ink-strong)]">
                  {acc.tenantName || acc.fullName}
                </span>
                <span className="block truncate text-sm text-[color:var(--color-priority-urgent)]">
                  {acc.status === 'organisation_unavailable'
                    ? 'Company unavailable'
                    : acc.status === 'invited'
                      ? 'Invitation pending'
                      : acc.status === 'suspended'
                        ? 'Account suspended'
                        : 'Account unavailable'}
                  {acc.reason ? ` — ${acc.reason}` : ''}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        onClick={onCancel}
        disabled={busy}
        className="text-sm font-medium text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)] disabled:opacity-60 cursor-pointer"
      >
        ← Back
      </button>
    </div>
  )
}
